import React, { useState, useEffect, useCallback } from 'react';
import './Overview.css';
import CustomDropdown from '../CustomDropdown';
import { ShimmerStatCard } from '../Shimmer';
import { API_BASE_URL } from '../../utils/api';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import { Line } from 'react-chartjs-2';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

// How far back the dashboard figures look.
const STATS_WINDOW_DAYS = 30;

function Overview({ onCreateTest, onNavigateToTests, onNavigateToSuites, onNavigateToScheduling, onNavigateToRuns, onRunTest, onSelectTest, selectedApplication, username }) {
  const [stats, setStats] = useState({
    totalRuns: 0,
    runsPassed: 0,
    runsFailed: 0,
    passRate: 0,
    avgDuration: 0
  });
  const [recentRuns, setRecentRuns] = useState([]);
  const [suiteHealth, setSuiteHealth] = useState([]);
  const [loading, setLoading] = useState(true);
  const [chartData, setChartData] = useState({
    labels: [],
    passed: [],
    failed: []
  });
  const [chartDays, setChartDays] = useState(7);
  const [chartLoading, setChartLoading] = useState(false);

  const fetchOverviewData = useCallback(async () => {
    if (!selectedApplication) return;
    
    const startTime = Date.now();
    const minLoadingTime = 2000;
    setLoading(true);
    
    try {
      // The dashboard reports on RUNS, not on how many tests exist.
      //
      // The cards used to show "Pass Rate (latest run)" and "Currently Failing",
      // which both describe a single moment: right after a green run they read
      // 100% and 0 even when the suite had failed 45 times that month. Averages
      // over the last 30 days say what the suite is actually like to live with.
      //
      // Dropped along the way: "Automation Coverage" (it counts test cases that
      // have code, and every recorded test does - permanently 100%) and the
      // test case count, which belongs on the Test Cases page.
      const runStatsResponse = await fetch(
        `${API_BASE_URL}/api/statistics/test-runs?application_id=${selectedApplication.id}&days=${STATS_WINDOW_DAYS}`
      );
      const runStatsData = await runStatsResponse.json();

      if (runStatsData && runStatsData.success) {
        const runStats = runStatsData.stats || {};

        setStats({
          totalRuns: runStats.totalRuns || 0,
          runsPassed: runStats.passed || 0,
          runsFailed: runStats.failed || 0,
          passRate: parseFloat(runStats.successRate) || 0,
          avgDuration: parseFloat(runStats.avgDuration) || 0
        });
      }
      
      // Fetch suite health
      const suiteResponse = await fetch(`${API_BASE_URL}/api/test-suites?application_id=${selectedApplication.id}`);
      const suiteData = await suiteResponse.json();
      
      if (suiteData.success && suiteData.testSuites) {
        const suites = suiteData.testSuites || [];
        const healthData = [];
        
        // Calculate pass rate for each suite
        for (const suite of suites) {
          try {
            const suiteDetailsResponse = await fetch(`${API_BASE_URL}/api/test-suites/${suite.id}?application_id=${selectedApplication.id}`);
            const suiteDetails = await suiteDetailsResponse.json();
            
            if (suiteDetails.success && suiteDetails.testSuite) {
              const testCaseIds = suiteDetails.testSuite.testCases?.map(tc => tc.id) || [];
              
              if (testCaseIds.length > 0) {
                // Get latest test runs for test cases in this suite
                const runsResponse = await fetch(`${API_BASE_URL}/api/test-runs?limit=1000&application_id=${selectedApplication.id}`);
                const runsData = await runsResponse.json();
                
                if (runsData.success) {
                  // A run counts towards this suite if it ran one of its test
                  // cases, OR if it was a run of the SUITE itself. Suite runs
                  // store a test_suite_id and no test_case_id, so matching only
                  // on test_case_id found nothing and every suite showed 0%.
                  const relevantRuns = runsData.testRuns.filter(run =>
                    testCaseIds.includes(run.test_case_id) || run.test_suite_id === suite.id
                  );

                  // Latest result per test case. A suite run gives the same
                  // result to every test case it contains.
                  const latestRuns = {};
                  const considerRun = (tcId, run) => {
                    if (!latestRuns[tcId] || normalizeDate(run.completed_at) > normalizeDate(latestRuns[tcId].completed_at)) {
                      latestRuns[tcId] = run;
                    }
                  };
                  relevantRuns.forEach(run => {
                    if (!run.completed_at) return;
                    if (run.test_case_id) considerRun(run.test_case_id, run);
                    else testCaseIds.forEach(tcId => considerRun(tcId, run));
                  });

                  const passed = Object.values(latestRuns).filter(r => r.status === 'passed').length;
                  const total = Object.keys(latestRuns).length;
                  const passRate = total > 0 ? Math.round((passed / total) * 100) : 0;
                  
                  healthData.push({
                    name: suite.name,
                    passRate
                  });
                }
              } else {
                healthData.push({
                  name: suite.name,
                  passRate: 0
                });
              }
            }
          } catch (e) {
            console.error(`Error fetching suite ${suite.id} details:`, e);
          }
        }
        
        setSuiteHealth(healthData);
      }
      
      // Fetch recent test runs
      const runsResponse = await fetch(`${API_BASE_URL}/api/test-runs?limit=10&application_id=${selectedApplication.id}`);
      const runsData = await runsResponse.json();
      
      if (runsData.success && runsData.testRuns) {
        const runs = runsData.testRuns.slice(0, 10).map(run => {
          const duration = run.duration ? `${(run.duration / 1000).toFixed(1)}s` : 'N/A';
          const timeAgo = run.completed_at ? getTimeAgo(normalizeDate(run.completed_at)) : 'N/A';
          
          return {
            id: run.id,
            status: run.status || 'pending',
            testName: run.test_name || run.test_case_name || 'Unknown Test',
            environment: 'Chrome',
            duration,
            triggeredBy: 'Manual',
            time: timeAgo
          };
        });
        
        setRecentRuns(runs);
      }
      
      // Chart data is now fetched separately in its own useEffect
    } catch (error) {
      console.error('Error fetching overview data:', error);
    } finally {
      const elapsed = Date.now() - startTime;
      const remaining = Math.max(0, minLoadingTime - elapsed);
      setTimeout(() => {
        setLoading(false);
      }, remaining);
    }
  }, [selectedApplication]);
  
  // Separate effect to fetch chart data when chartDays changes (without reloading entire page)
  useEffect(() => {
    if (!selectedApplication) return;
    
    const fetchChartDataOnly = async () => {
      setChartLoading(true);
      const startTime = Date.now();
      const minLoadingTime = 1000; // Minimum 1 second loading time
      
      try {
        const chartResponse = await fetch(`${API_BASE_URL}/api/statistics/test-runs-by-date?application_id=${selectedApplication.id}&days=${chartDays}`);
        const chartDataResponse = await chartResponse.json();
        
        if (chartDataResponse.success && chartDataResponse.data) {
          setChartData(chartDataResponse.data);
        } else {
          setChartData({
            labels: [],
            passed: [],
            failed: []
          });
        }
      } catch (chartError) {
        console.error('Error fetching chart data:', chartError);
        setChartData({
          labels: [],
          passed: [],
          failed: []
        });
      } finally {
        // Ensure minimum loading time of 1 second
        const elapsed = Date.now() - startTime;
        const remaining = Math.max(0, minLoadingTime - elapsed);
        setTimeout(() => {
          setChartLoading(false);
        }, remaining);
      }
    };
    
    fetchChartDataOnly();
  }, [selectedApplication, chartDays]);
  
  useEffect(() => {
    if (selectedApplication) {
      fetchOverviewData();
    } else {
      setStats({ totalRuns: 0, runsPassed: 0, runsFailed: 0, passRate: 0, avgDuration: 0 });
      setRecentRuns([]);
      setSuiteHealth([]);
      setLoading(false);
    }
  }, [selectedApplication, fetchOverviewData]);
  
  // Timestamps come back as "2026-09-17 10:33:00" - UTC, but with nothing to
  // say so. Passed to new Date() they are read as LOCAL time, which in IST made
  // every run look 5h30m older than it was. Mark them as UTC first.
  const normalizeDate = (dateStr) => {
    if (!dateStr) return null;
    if (dateStr instanceof Date) return dateStr;
    const lacksTimezone = typeof dateStr === 'string' && !/[zZ]|[+-]\d{2}:?\d{2}$/.test(dateStr);
    const normalized = lacksTimezone ? String(dateStr).replace(' ', 'T') + 'Z' : dateStr;
    const parsed = new Date(normalized);
    return isNaN(parsed.getTime()) ? null : parsed;
  };

  const getTimeAgo = (date) => {
    if (!date || isNaN(date.getTime())) return 'N/A';
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} ${diffMins === 1 ? 'min' : 'mins'} ago`;
    if (diffHours < 24) return `${diffHours} ${diffHours === 1 ? 'hour' : 'hours'} ago`;
    return `${diffDays} ${diffDays === 1 ? 'day' : 'days'} ago`;
  };

  // Get time-based greeting (updates based on current time)
  const getGreeting = () => {
    const hour = new Date().getHours();
    // 0-11: Good morning (before 12 PM)
    // 12-17: Good afternoon (12 PM to 5:59 PM)
    // 18-23: Good evening (6 PM onwards)
    if (hour < 12) return 'Good morning';
    if (hour < 18) return 'Good afternoon';
    return 'Good evening';
  };

  const getDisplayName = () => {
    if (!username || username === 'User' || username === 'Alex Morgan') return 'Alex Morgan';
    
    // Remove email domain if present
    let displayName = username.split('@')[0];
    
    // Split by dots, spaces, underscores, or hyphens
    const parts = displayName.split(/[.\s_-]+/).filter(part => part.length > 0);
    
    // Capitalize first letter of each part and join with space
    const formatted = parts.map(part => {
      // Remove any remaining special characters, keep only letters
      const cleanPart = part.replace(/[^a-zA-Z]/g, '');
      if (cleanPart.length > 0) {
        return cleanPart.charAt(0).toUpperCase() + cleanPart.slice(1).toLowerCase();
      }
      return '';
    }).filter(part => part.length > 0).join(' '); // Join with space
    
    return formatted || 'Alex Morgan';
  };

  const getStatusColor = (status) => {
    return status === 'passed' ? '#10B981' : '#EF4444';
  };

  const getStatusBg = (status) => {
    return status === 'passed' ? '#D1FAE5' : '#FEE2E2';
  };

  if (loading) {
    return (
      <div className="overview">
        <div className="overview-header">
          <div className="shimmer-line shimmer-title"></div>
        </div>
        <div className="shimmer-overview-stats">
          <ShimmerStatCard />
          <ShimmerStatCard />
          <ShimmerStatCard />
          <ShimmerStatCard />
        </div>
      </div>
    );
  }

  return (
    <div className="overview">
      {/* Greeting Section with Action Buttons */}
      <div className="greeting-section">
        <div className="greeting-content">
          <h1 className="greeting-text">
            {getGreeting()}, <span className="greeting-name">{getDisplayName() + "!"}</span>
          </h1>
          <p className="greeting-subtitle">Here's what's happening with your test suites today.</p>
        </div>
        {/* Action Buttons */}
        <div className="action-buttons">
          {/* Scheduling is where a suite can actually be run on demand;
              Test Suites only lists and edits them. */}
          <button className="btn-run-suite" onClick={onNavigateToScheduling}>
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M3 2L13 8L3 14V2Z" fill="currentColor" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            Run Suite
          </button>
          <button className="btn-record-test" onClick={onCreateTest}>
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <circle cx="8" cy="8" r="5" fill="currentColor" stroke="currentColor" strokeWidth="1.5"/>
            </svg>
            Record Test
          </button>
        </div>
      </div>

      {/* Statistics Cards */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-header">
            <span className="stat-label">Pass Rate ({STATS_WINDOW_DAYS} days)</span>
            <div className="stat-icon green">
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path d="M16 6L8 14L4 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
          </div>
          <div className="stat-value">{stats.passRate}%</div>
          <div className="stat-change">
            <span>across {stats.totalRuns} {stats.totalRuns === 1 ? 'run' : 'runs'}</span>
          </div>

        </div>

        <div className="stat-card">
          <div className="stat-header">
            <span className="stat-label">Test Runs ({STATS_WINDOW_DAYS} days)</span>
            <div className="stat-icon red">
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <circle cx="10" cy="10" r="8" stroke="currentColor" strokeWidth="1.5"/>
                <path d="M10 6V10M10 14H10.01" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
              </svg>
            </div>
          </div>
          <div className="stat-value">{stats.totalRuns.toLocaleString()}</div>
          <div className="stat-change">
            <span>{stats.runsPassed} passed &middot; {stats.runsFailed} failed</span>
          </div>

        </div>

        <div className="stat-card">
          <div className="stat-header">
            <span className="stat-label">Avg Duration</span>
            <div className="stat-icon purple">
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <circle cx="10" cy="10" r="8" stroke="currentColor" strokeWidth="1.5"/>
                <path d="M10 2C12.5 2 14.5 4 14.5 6.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
              </svg>
            </div>
          </div>
          <div className="stat-value">{stats.avgDuration}s</div>
          <div className="stat-change">
            <span>per run, last {STATS_WINDOW_DAYS} days</span>
          </div>
        </div>
      </div>

      {/* Main Content Grid */}
      <div className="overview-content-grid">
        {/* Execution Trends */}
        <div className="trends-section">
          <div className="section-header">
            <h2 className="section-title">Execution Trends</h2>
            <CustomDropdown
              value={`Last ${chartDays} Days`}
              style={{ width: '10%' }}
              onChange={(e) => {
                const selectedValue = e.target.value;
                const days = parseInt(selectedValue.replace('Last ', '').replace(' Days', ''));
                if (!isNaN(days) && days !== chartDays) {
                  setChartDays(days);
                  // Chart data will be refetched automatically via useEffect dependency
                }
              }}
              options={[
                { value: 'Last 7 Days', label: 'Last 7 Days' },
                { value: 'Last 30 Days', label: 'Last 30 Days' },
                { value: 'Last 90 Days', label: 'Last 90 Days' }
              ]}
              className="time-selector"
            />
          </div>
          <div className="chart-container">
            {chartLoading ? (
              <div className="chart-shimmer">
                <div className="shimmer-chart-area">
                  {/* Simulate chart lines */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', width: '100%', height: '100%', justifyContent: 'flex-end', paddingBottom: '2rem' }}>
                    {[0, 1, 2, 3, 4, 5, 6].map((i) => (
                      <div key={i} className="shimmer-chart-line" style={{ 
                        width: `${85 + (i * 2)}%`, 
                        height: '20px',
                        marginLeft: 'auto',
                        marginRight: 'auto'
                      }}></div>
                    ))}
                  </div>
                  {/* Simulate legend */}
                  <div className="shimmer-chart-legend">
                    <div className="shimmer-line" style={{ width: '60px', height: '12px' }}></div>
                    <div className="shimmer-line" style={{ width: '60px', height: '12px' }}></div>
                  </div>
                  <div className="shimmer-shine"></div>
                </div>
              </div>
            ) : chartData.labels && chartData.labels.length > 0 ? (
              <Line
                data={{
                  labels: chartData.labels,
                  datasets: [
                    {
                      label: 'Passed',
                      data: chartData.passed,
                      borderColor: '#10B981',
                      backgroundColor: 'rgba(16, 185, 129, 0.1)',
                      fill: true,
                      tension: 0.4,
                      pointRadius: 4,
                      pointHoverRadius: 6,
                      pointBackgroundColor: '#10B981',
                      pointBorderColor: '#ffffff',
                      pointBorderWidth: 2
                    },
                    {
                      label: 'Failed',
                      data: chartData.failed,
                      borderColor: '#EF4444',
                      backgroundColor: 'rgba(239, 68, 68, 0.1)',
                      fill: true,
                      tension: 0.4,
                      pointRadius: 4,
                      pointHoverRadius: 6,
                      pointBackgroundColor: '#EF4444',
                      pointBorderColor: '#ffffff',
                      pointBorderWidth: 2
                    }
                  ]
                }}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: {
                    legend: {
                      display: true,
                      position: 'top',
                      labels: {
                        usePointStyle: true,
                        padding: 15,
                        font: {
                          size: 12,
                          weight: '500'
                        }
                      }
                    },
                    tooltip: {
                      mode: 'index',
                      intersect: false,
                      backgroundColor: 'rgba(0, 0, 0, 0.8)',
                      padding: 12,
                      titleFont: {
                        size: 13,
                        weight: '600'
                      },
                      bodyFont: {
                        size: 12
                      },
                      callbacks: {
                        label: function(context) {
                          return `${context.dataset.label}: ${context.parsed.y} test${context.parsed.y !== 1 ? 's' : ''}`;
                        }
                      }
                    }
                  },
                  scales: {
                    x: {
                      grid: {
                        display: false
                      },
                      ticks: {
                        font: {
                          size: 11
                        },
                        color: '#6B7280'
                      }
                    },
                    y: {
                      beginAtZero: true,
                      grid: {
                        color: '#E5E7EB',
                        drawBorder: false
                      },
                      ticks: {
                        stepSize: 1,
                        font: {
                          size: 11
                        },
                        color: '#6B7280'
                      }
                    }
                  },
                  interaction: {
                    mode: 'nearest',
                    axis: 'x',
                    intersect: false
                  }
                }}
              />
            ) : (
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center', 
                height: '250px',
                color: '#6B7280',
                fontSize: '14px'
              }}>
                No execution data available
              </div>
            )}
          </div>
        </div>

        {/* Suite Health */}
        <div className="suite-health-section">
          <div className="section-header">
            <h2 className="section-title">Suite Health</h2>
            <button className="view-all-link" onClick={onNavigateToSuites}>
              View All Suites
            </button>
          </div>
          <div className="suite-list">
            {suiteHealth.map((suite, index) => (
              <div key={index} className="suite-health-item">
                <div className="suite-name">{suite.name}</div>
                <div className="suite-pass-rate">{suite.passRate}% Pass</div>
                <div className="suite-progress-bar">
                  <div
                    className={`suite-progress ${
                      suite.passRate >= 90 ? 'high' : suite.passRate >= 70 ? 'medium' : 'low'
                    }`}
                    style={{ width: `${suite.passRate}%` }}
                  ></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Recent Test Runs */}
      <div className="recent-runs-section">
        <div className="section-header">
          <h2 className="section-title">Recent Test Runs</h2>
          <button className="view-all-link" onClick={onNavigateToRuns}>
            View All Runs
          </button>
        </div>
        <div className="runs-table-container">
          <table className="runs-table">
            <thead>
              <tr>
                <th>STATUS</th>
                <th>TEST NAME</th>
                <th>ENVIRONMENT</th>
                <th>DURATION</th>
                <th>TRIGGERED BY</th>
                <th>TIME</th>
              </tr>
            </thead>
            <tbody>
              {recentRuns && recentRuns.length ===  0 ? (
                <tr>
                  <td style={{ textAlign: 'center', padding: '2rem', color: 'rgb(107, 114, 128)' }}>No recent test runs found.</td>
                </tr>
              ) : (
                recentRuns.map((run) => (
                <tr key={run.id}>
                  <td>
                    <span
                      className="status-badge"
                      style={{
                        backgroundColor: getStatusBg(run.status),
                        color: getStatusColor(run.status)
                      }}
                    >
                      {run.status === 'passed' ? 'Passed' : 'Failed'}
                    </span>
                  </td>
                  <td className="test-name">{run.testName}</td>
                  <td>{run.environment}</td>
                  <td>{run.duration}</td>
                  <td>{run.triggeredBy}</td>
                  <td>{run.time}</td>
                </tr>
              )))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default Overview;
